import React, { useState } from 'react';
import {
  Home,
  User,
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Users,
  Heart,
  BookOpen,
  Menu,
  X
} from 'lucide-react';
import { useOrganization } from '../hooks/useOrganization';

interface SidebarProps {
  currentPath?: string;
  onToggle?: (isExpanded: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ currentPath, onToggle }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { isOfficialOrganization, hasChurchAccess } = useOrganization();

  const toggleSidebar = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    onToggle?.(newState);
  };

  const baseMenuItems = [
    {
      path: '/feed',
      label: 'Feed',
      icon: Home
    },
    {
      path: '/missoes',
      label: 'Missoes',
      icon: Heart
    },
    {
      path: '/devocional',
      label: 'Devocional',
      icon: BookOpen
    }
  ];

  const churchMenuItems = hasChurchAccess ? [
    {
      path: '/dizimos',
      label: 'Gestão de Cobranças',
      icon: DollarSign
    },
    {
      path: '/assinantes',
      label: 'Usuários',
      icon: Users
    }
  ] : [];

  const profilesMenuItem = isOfficialOrganization ? [{
    path: '/profiles',
    label: 'Perfis',
    icon: User
  }] : [];

  const menuItems = [...baseMenuItems, ...churchMenuItems, ...profilesMenuItem];

  const handleNavigation = (path: string) => {
    setMobileOpen(false);
    window.location.href = path;
  };

  const renderMenu = () => (
    <nav className="mt-4">
      {menuItems.map((item) => {
        const IconComponent = item.icon;
        const isActive = currentPath === item.path;

        return (
          <button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-gold-50 ${
              isActive
                ? 'bg-gold-100 text-gold-800 border-r-4 border-gold-500'
                : 'text-gray-700 hover:text-gold-700'
            }`}
          >
            <IconComponent
              size={20}
              className={`${
                isActive ? 'text-gold-600' : 'text-gray-500'
              } ${!isExpanded ? 'mx-auto' : ''}`}
            />
            {(isExpanded || mobileOpen) && (
              <span className="font-medium">{item.label}</span>
            )}
          </button>
        );
      })}
    </nav>
  );

  return (
    <>
      {/* Mobile header bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <img src="/santogram_logo.png" alt="SantoGram" className="w-8 h-8" />
          <h1 className="text-lg font-bold text-brown-800">SantoGram</h1>
        </div>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          {mobileOpen ? <X size={24} className="text-gray-600" /> : <Menu size={24} className="text-gray-600" />}
        </button>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer */}
      <div className={`md:hidden fixed top-14 left-0 bottom-0 w-64 bg-white shadow-lg z-50 transition-transform duration-300 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {renderMenu()}
        <div className="absolute bottom-4 left-4 right-4">
          <div className="text-xs text-gray-500 text-center">
            <p>SantoGram &copy; 2025</p>
            <p>Versão 1.0</p>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className={`hidden md:block fixed left-0 top-0 h-full bg-white shadow-lg transition-all duration-300 z-50 ${
        isExpanded ? 'w-64' : 'w-16'
      }`}>
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          {isExpanded && (
            <div className="flex items-center gap-3">
              <img src="/santogram_logo.png" alt="SantoGram" className="w-8 h-8" />
              <h1 className="text-xl font-bold text-brown-800">SantoGram</h1>
            </div>
          )}
          {!isExpanded && (
            <img src="/santogram_logo.png" alt="SantoGram" className="w-8 h-8 mx-auto" />
          )}
          <button
            onClick={toggleSidebar}
            className="p-1 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isExpanded ? (
              <ChevronLeft size={20} className="text-gray-600" />
            ) : (
              <ChevronRight size={20} className="text-gray-600" />
            )}
          </button>
        </div>

        {renderMenu()}

        {isExpanded && (
          <div className="absolute bottom-4 left-4 right-4">
            <div className="text-xs text-gray-500 text-center">
              <p>SantoGram &copy; 2025</p>
              <p>Versão 1.0</p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;
